/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 25-08-2026
 *
 * Root-service voor het tweede docentendashboard (golf 3g). Draagt de open-state (de
 * modeltoolbar zet hem, het paneel leest hem — twee sibling-eilanden) en de opgehaalde
 * studentmodellen met hun actiehistorie, die deels via de notify-berichten binnenkomen; daarop
 * luistert de service zelf.
 *
 * De haalronde staat hier omdat hij uit drie stappen bestaat die bij elkaar horen: de relevante
 * modellen ophalen voor de gekozen eindgrens, van de nieuwe modellen de actiedata bijhalen, en
 * het resultaat samenvoegen. De generatiebewaking eromheen is geen signal-vanzelfsprekendheid:
 * een openingsronde, een wijziging van de periode en een binnengekomen bericht over een onbekend
 * model kunnen elkaar overlappen, en het antwoord op een inmiddels achterhaalde vraag gaat weg.
 *
 * Tijd is hier epoch-ms, ook op de grens: sinds golf b0-2 neemt `v2relevantModels` het
 * `end`-argument als tijdstip in milliseconden. Daarmee is de laatste tijdzone-afhankelijkheid uit
 * deze service verdwenen — het backend las die grens eerder als Amsterdamse wandtijd, waardoor hij
 * buiten Nederland verschoof.
 */
import {inject, Injectable, signal} from '@angular/core';
import {DateTime} from 'luxon';
import {Sihwlog} from 'sihw-ngx';
import {rolUitBackend, type SamenwerkRol} from './samenwerking.service';
import {ApiService} from '../app/backend/api.service';

/**
 * Eén modelrij zoals `dashboard`/`v2relevantModels` hem teruggeeft. De veldnamen zijn die van
 * het backend; de omzetting naar `Dashboardv2Model` gebeurt hieronder, op de grens.
 */
export interface Dashboardv2ModelWire {
    id: string;
    titel: string;
    user: string;
    live: boolean;
    /** ISO-8601 mét zone-aanduiding (Sequelize-`DATE`). */
    createdAt: string;
    /** De rol in de wire-vorm: `single`, `master` of `slave`. */
    samenwerking: string;
    /** Het model waar deze sub onder hangt. */
    samenwerking_main: string | null;
}

/**
 * De actie- en fouthistorie van één model zoals het backend hem levert — zowel als antwoord op
 * `v2actiondata` als in een `dashboardactionsv2`-notify.
 */
export interface Dashboardv2ActiedataWire {
    actions?: {action: string; timestamp: number}[];
    errors?: {timestamp: number; count: Dashboardv2Telling}[];
}

/**
 * Eén studentmodel in het overzicht.
 */
export interface Dashboardv2Model {
    id: string;
    titel: string;
    user: string;
    /** Of iemand dit model op dit moment openheeft. */
    live: boolean;
    /** De rol van dit model in een samenwerking. */
    rol: SamenwerkRol;
    /** Het model waar deze sub onder hangt, of `null`. */
    hoofdmodel: string | null;
    /** Wanneer het model is aangemaakt, in epoch-ms. */
    aangemaakt: number;
}

/**
 * De stand van de normtelling op één moment: hoeveel de norm verwacht, hoeveel het model heeft
 * en hoeveel daarvan fout is.
 */
export interface Dashboardv2Telling {
    totaal: number;
    aantal: number;
    fout: number;
}

/**
 * Eén actie van een student.
 */
export interface Dashboardv2Actie {
    actie: string;
    /** Het tijdstip in epoch-ms. */
    tijd: number;
}

/**
 * Eén normtelling-meting.
 */
export interface Dashboardv2Fout {
    /** Het tijdstip in epoch-ms. */
    tijd: number;
    telling: Dashboardv2Telling;
}

/**
 * Wat er van één model getekend wordt: de acties als tikjes en de tellingen als twee lijnen.
 */
export interface Dashboardv2Actiedata {
    acties: readonly Dashboardv2Actie[];
    fouten: readonly Dashboardv2Fout[];
}

//de meekijk-keten die AngularJS hier registreerde (`Dashboardv2Acties`) is met golf 4c vervallen:
//het paneel roept `ModelbeheerService.meekijken` rechtstreeks aan

@Injectable({providedIn: 'root'})
export class Dashboardv2Service {

    /**
     * De logger. Bewust op `debug`: de wire-vormcontrole hieronder moet in de testronde in de
     * console te zien zijn.
     */
    private readonly log = new Sihwlog('debug', 'Dashboardv2Service');

    /**
     * De backendlaag: het abonnement op de actieberichten en de haalronde.
     */
    private readonly api = inject(ApiService);

    /**
     * Of het paneel openstaat.
     */
    private readonly _open = signal(false);

    /**
     * De opgehaalde studentmodellen, in de volgorde waarin ze binnenkwamen. De sortering is
     * state van het paneel.
     */
    private readonly _modellen = signal<readonly Dashboardv2Model[]>([]);

    /**
     * De actie- en fouthistorie per model-id.
     */
    private readonly _actiedata = signal<Readonly<Record<string, Dashboardv2Actiedata>>>({});

    /**
     * Het aanmaakmoment van het oudste opgehaalde model, in epoch-ms, of `null` als er nog geen
     * modellen zijn. Het paneel trekt zijn startveld hiernaartoe op, zodat de docent ziet dat er
     * niets ouders is.
     */
    private readonly _oudsteModel = signal<number | null>(null);

    /**
     * Telt de afgeronde haalrondes. Het paneel hersorteert hierop: de volgorde is geen computed
     * over de binnenkomende data, want dan zouden de blokken bij elke push onder de handen van
     * de docent rondspringen.
     */
    private readonly _haalronde = signal(0);

    /**
     * Of er een haalronde loopt. Vervangt het blokkerende `sihwWait`-scherm uit ng1: het paneel
     * toont er een eigen laadmelding mee.
     */
    private readonly _bezig = signal(false);

    /**
     * De eindgrens van de laatst gestelde vraag. Het paneel bezit de periode, maar de service
     * moet hem vasthouden: ook een bericht over een onbekend model leidt tot een haalronde, en
     * die hoort dezelfde grens te gebruiken.
     */
    private eindgrens: DateTime | null = null;

    /**
     * Generatienummer van de lopende haalronde.
     */
    private generatie = 0;

    /**
     * Modellen waarover een bericht binnenkwam terwijl we ze niet kenden. Daar doen we één
     * haalronde voor; daarna klopt het beeld zolang de periode niet wijzigt.
     */
    private nieuweModellen = new Set<string>();

    /**
     * Of de wire-vormcontrole al gelogd is (zie `logWirevormen`).
     */
    private wirevormenGelogd = false;

    /**
     * Of het paneel openstaat.
     */
    readonly open = this._open.asReadonly();

    /**
     * De opgehaalde studentmodellen.
     */
    readonly modellen = this._modellen.asReadonly();

    /**
     * De actie- en fouthistorie per model-id.
     */
    readonly actiedata = this._actiedata.asReadonly();

    /**
     * Het aanmaakmoment van het oudste opgehaalde model, in epoch-ms.
     */
    readonly oudsteModel = this._oudsteModel.asReadonly();

    /**
     * Het volgnummer van de laatst afgeronde haalronde.
     */
    readonly haalronde = this._haalronde.asReadonly();

    /**
     * Of er een haalronde loopt.
     */
    readonly bezig = this._bezig.asReadonly();

    constructor() {
        //de twee notificaties waarmee het backend dit dashboard bijwerkt, en het verzoek om ons na een
        //hapering opnieuw aan te melden. Tot golf 4e gaf de AngularJS-hoofdcontroller ze door
        this.api.onNotify<string[]>('dashboardlive').subscribe(live => this.zetLive(live));
        this.api.onNotify<Dashboardv2ActiedataWire & {model: string}>('dashboardactionsv2')
            .subscribe(data => this.zetActies(data.model, data));
        this.api.onNotify('registratieVerzoek').subscribe(() => this.meldHerabonneren());
    }

    /* ******** Openen en sluiten ******** */

    /**
     * Opent het paneel en meldt zich aan voor de actieberichten. Doet niets als het al
     * openstaat. De rechtencheck ligt bij de knop in de modeltoolbar, die zonder het
     * `dashboard2`-recht niet bestaat.
     *
     * De eerste haalronde volgt uit het paneel: dat bezit de periode en bepaalt hem vers bij
     * elke opening, precies zoals ng1 dat in `api.open()` deed.
     */
    openen(): void {
        if (this._open()) {
            return;
        }
        this._open.set(true);
        void this.api.dashboardAbonneerV2();
    }

    /**
     * Sluit het paneel en meldt zich af. Doet niets als het al dicht is, zodat het meekijken
     * (`ModelbeheerService`) hem onvoorwaardelijk kan aanroepen. De opgehaalde data blijft staan;
     * bij een heropening volgt sowieso een verse haalronde.
     */
    sluiten(): void {
        if (!this._open()) {
            return;
        }
        this._open.set(false);
        void this.api.dashboardZegOpV2();
    }

    /**
     * Het backend vraagt om een verse registratie (na een hapering). Alleen zin als we open
     * staan.
     */
    meldHerabonneren(): void {
        if (this._open()) {
            void this.api.dashboardAbonneerV2();
        }
    }

    /* ******** De periode en de haalronde ******** */

    /**
     * Het paneel meldt de eindgrens van zijn periode. Alleen die grens is een parameter van de
     * vraag aan het backend; het startmoment bepaalt puur wat er getekend wordt en blijft dus
     * bij het paneel.
     */
    zetPeriode(eind: DateTime): void {
        this.eindgrens = eind;
    }

    /**
     * Haalt de modellen en hun actiedata opnieuw op.
     * @param opnieuw gooi eerst alles weg — na een wijziging van de periode klopt de bestaande
     *   verzameling niet meer
     */
    async haalOpnieuw(opnieuw: boolean): Promise<void> {
        if (!this.eindgrens) {
            return;
        }
        const generatie = ++this.generatie;
        this._bezig.set(true);
        try {
            const rijen = await this.api.dashboardRelevanteModellen(this.eindgrens.toMillis());
            if (generatie !== this.generatie) {
                return; //er staat inmiddels een nieuwere vraag uit
            }
            if (opnieuw) {
                this._modellen.set([]);
                this._actiedata.set({});
                this.nieuweModellen.clear();
            }
            const bekend = new Set(this._modellen().map(model => model.id));
            const verse = (rijen ?? []).filter(rij => !bekend.has(rij.id));
            if (verse.length) {
                const data = await this.api.dashboardActiedata(verse.map(rij => rij.id));
                if (generatie !== this.generatie) {
                    return;
                }
                const actiedata = {...this._actiedata()};
                for (const rij of verse) {
                    actiedata[rij.id] = this.leesActiedata(data?.[rij.id]);
                }
                this._actiedata.set(actiedata);
                this.logWirevormen(verse[0], data?.[verse[0].id]);
            }
            const modellen = [...this._modellen(), ...verse.map(rij => this.leesModel(rij))];
            this._modellen.set(modellen);
            this._oudsteModel.set(modellen.length ? Math.min(...modellen.map(model => model.aangemaakt)) : null);
            this._haalronde.update(ronde => ronde + 1);
        } catch (fout) {
            //het backend is even niet bereikbaar: het paneel houdt de stand die het had
            this.log.error('Ophalen dashboardgegevens mislukt', fout);
        } finally {
            if (generatie === this.generatie) {
                this._bezig.set(false);
            }
        }
    }

    /* ******** De spiegel: gevuld door de notify-abonnementen in de constructor ******** */

    /**
     * De lijst met modellen die op dit moment openstaan is gewijzigd. Modellen die we nog niet
     * kennen zijn mogelijk nieuw en leiden tot een haalronde.
     */
    zetLive(live: readonly string[]): void {
        if (!this._open()) {
            return;
        }
        const open = new Set(live ?? []);
        this._modellen.update(modellen => modellen.map(model => ({...model, live: open.has(model.id)})));
        void this.checkNieuweModellen(live ?? []);
    }

    /**
     * Er zijn nieuwe acties voor een model binnengekomen. Voor een model dat we niet kennen
     * gooien we de meegezonden acties bewust weg: na het bijhalen komt de volledige historie
     * via `v2actiondata` binnen, en dan zouden ze dubbel staan.
     */
    zetActies(modelId: string, wire: Dashboardv2ActiedataWire): void {
        if (!this._open()) {
            return;
        }
        const huidig = this._actiedata()[modelId];
        if (!huidig) {
            void this.checkNieuweModellen([modelId]);
            return;
        }
        const verse = this.leesActiedata(wire);
        //een push op de bestaande array is voor een signal geen wijziging: nieuwe lijsten dus
        this._actiedata.update(actiedata => ({
            ...actiedata,
            [modelId]: {
                acties: [...huidig.acties, ...verse.acties],
                fouten: [...huidig.fouten, ...verse.fouten]
            }
        }));
    }

    /* ******** Privé ******** */

    /**
     * Zet een modelrij van het backend om naar de vorm waarmee ng21 werkt.
     */
    private leesModel(rij: Dashboardv2ModelWire): Dashboardv2Model {
        return {
            id: rij.id,
            titel: rij.titel,
            user: rij.user,
            live: !!rij.live,
            rol: rolUitBackend(rij.samenwerking),
            hoofdmodel: rij.samenwerking_main ?? null,
            aangemaakt: DateTime.fromISO(rij.createdAt).toMillis()
        };
    }

    /**
     * Zet de actie- en fouthistorie van het backend om naar de vorm waarmee ng21 werkt. De
     * `timestamp` is een `BIGINT`-kolom; mysql2 levert hem als getal, maar `Number(...)` dekt een
     * driver- of configuratiewissel af.
     */
    private leesActiedata(wire: Dashboardv2ActiedataWire | undefined): Dashboardv2Actiedata {
        return {
            acties: (wire?.actions ?? []).map(actie => ({actie: actie.action, tijd: Number(actie.timestamp)})),
            fouten: (wire?.errors ?? []).map(fout => ({tijd: Number(fout.timestamp), telling: fout.count}))
        };
    }

    /**
     * Logt eenmalig de ruwe vorm van de twee tijdvelden die over de lijn komen. Dat `createdAt`
     * ISO-8601 mét zone is en `timestamp` een getal, is uit de codeketen afgeleid en niet tegen
     * een draaiende server getoetst; deze regel maakt dat in de testronde controleerbaar.
     */
    private logWirevormen(rij: Dashboardv2ModelWire, data: Dashboardv2ActiedataWire | undefined): void {
        if (this.wirevormenGelogd) {
            return;
        }
        const eersteActie = data?.actions?.[0];
        this.log.debug(`Wire-vormen`, {
            createdAt: rij.createdAt,
            timestamp: eersteActie ? typeof eersteActie.timestamp : 'geen actie in deze ronde'
        });
        this.wirevormenGelogd = !!eersteActie; //zonder actie nog een ronde wachten
    }

    /**
     * Zoekt in de opgegeven id's naar modellen die we nog niet kennen en haalt daar één keer
     * alles voor op.
     */
    private async checkNieuweModellen(ids: readonly string[]): Promise<void> {
        const actiedata = this._actiedata();
        const onbekend = ids.filter(id => id && !actiedata[id] && !this.nieuweModellen.has(id));
        if (!onbekend.length) {
            return;
        }
        for (const id of onbekend) {
            this.nieuweModellen.add(id);
        }
        await this.haalOpnieuw(false);
    }
}
